import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Users, 
  Calendar, 
  CreditCard, 
  BarChart3, 
  Shield, 
  Zap, 
  Check,
  ArrowRight,
  Scan,
  Sparkles
} from 'lucide-react';

const features = [
  { icon: Users, title: 'Gestión de Clientes', desc: 'Base de datos completa con historial y membresías' },
  { icon: Calendar, title: 'Agenda Inteligente', desc: 'Sistema de citas con vistas diaria, semanal y mensual' },
  { icon: CreditCard, title: 'Control de Membresías', desc: 'Alertas automáticas de vencimiento y renovación' },
  { icon: BarChart3, title: 'Reportes Avanzados', desc: 'Métricas de ingresos, clientes y servicios' },
  { icon: Shield, title: 'Seguridad Total', desc: 'Datos encriptados y respaldos automáticos' },
  { icon: Scan, title: 'Check-in Rápido', desc: 'Sistema de acceso con códigos únicos' },
];

const plans = [
  {
    name: 'Starter',
    price: '299',
    desc: 'Para negocios pequeños',
    features: ['Hasta 100 clientes', 'Agenda básica', '1 usuario', 'Soporte email'],
    popular: false
  },
  {
    name: 'Pro',
    price: '599',
    desc: 'Para negocios en crecimiento',
    features: ['Hasta 500 clientes', 'Agenda avanzada', '5 usuarios', 'Reportes', 'Check-in', 'Soporte prioritario'],
    popular: true
  },
  {
    name: 'Business',
    price: '999',
    desc: 'Para grandes operaciones',
    features: ['Clientes ilimitados', 'Multi-sucursal', 'Usuarios ilimitados', 'API', 'Integraciones', 'Soporte 24/7'],
    popular: false
  },
];

const container = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

const item = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1] } }
};

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#0c0c0d]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#0c0c0d]/80 backdrop-blur-xl border-b border-white/[0.04]">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-indigo-500/20">
              <span className="text-white font-bold text-sm">N</span>
            </div>
            <span className="text-lg font-semibold text-white tracking-tight">Nova</span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/login" className="text-sm font-medium text-[#a1a1a6] hover:text-white transition-colors" data-testid="login-link">
              Iniciar sesión
            </Link>
            <Link to="/register" className="nova-btn nova-btn-primary py-2 px-4 text-sm" data-testid="register-link">
              Prueba gratis
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="pt-32 pb-24 px-6 relative overflow-hidden">
        <div className="absolute inset-0 nova-grid-pattern opacity-30" />
        <div className="absolute top-20 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-indigo-500/10 rounded-full blur-[120px] pointer-events-none" />
        
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-4xl mx-auto text-center relative"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-500/10 border border-indigo-500/20 rounded-full mb-8">
            <Sparkles className="w-4 h-4 text-indigo-400" />
            <span className="text-sm text-indigo-300 font-medium">14 días de prueba gratis</span>
          </div>
          
          <h1 className="text-5xl md:text-7xl font-bold text-white tracking-tight leading-[1.1] mb-6">
            Gestiona tu negocio
            <br />
            <span className="text-[#6e6e73]">como un profesional</span>
          </h1>
          
          <p className="text-lg md:text-xl text-[#a1a1a6] max-w-2xl mx-auto mb-12 leading-relaxed">
            Nova es la plataforma todo-en-uno para gimnasios, spas, clínicas y negocios de servicios. 
            Clientes, citas, membresías y métricas en un solo lugar.
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/register" className="nova-btn nova-btn-primary px-8 py-4 text-base group" data-testid="hero-cta">
              Comenzar gratis
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link to="/login" className="nova-btn nova-btn-secondary px-8 py-4 text-base">
              Ver demo
            </Link>
          </div>
        </motion.div>
      </section>

      {/* Features */}
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Todo lo que necesitas
            </h2>
            <p className="text-[#a1a1a6] max-w-xl mx-auto">
              Herramientas diseñadas para simplificar tu negocio
            </p>
          </motion.div>

          <motion.div 
            variants={container}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5"
          >
            {features.map((feature) => (
              <motion.div
                key={feature.title}
                variants={item}
                className="nova-card nova-card-hover p-6"
              >
                <div className="w-12 h-12 rounded-xl bg-indigo-500/10 flex items-center justify-center mb-5">
                  <feature.icon className="w-6 h-6 text-indigo-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
                <p className="text-sm text-[#6e6e73] leading-relaxed">{feature.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-24 px-6 bg-[#111113]" id="pricing">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Planes transparentes
            </h2>
            <p className="text-[#a1a1a6] max-w-xl mx-auto">
              Sin costos ocultos. Cancela cuando quieras.
            </p>
          </motion.div>

          <motion.div 
            variants={container}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto"
          >
            {plans.map((plan) => (
              <motion.div
                key={plan.name}
                variants={item}
                className={`relative p-8 rounded-2xl transition-all ${
                  plan.popular 
                    ? 'bg-gradient-to-b from-indigo-500 to-indigo-600 text-white shadow-2xl shadow-indigo-500/25 scale-105' 
                    : 'nova-card'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-white text-indigo-600 text-xs font-bold rounded-full shadow-lg">
                    Más popular
                  </div>
                )}
                <div className="mb-6">
                  <h3 className={`text-lg font-semibold mb-1 ${plan.popular ? 'text-white' : 'text-white'}`}>
                    {plan.name}
                  </h3>
                  <p className={`text-sm ${plan.popular ? 'text-indigo-100' : 'text-[#6e6e73]'}`}>
                    {plan.desc}
                  </p>
                </div>
                <div className="mb-8">
                  <span className={`text-5xl font-bold ${plan.popular ? 'text-white' : 'text-white'}`}>
                    ${plan.price}
                  </span>
                  <span className={`text-sm ${plan.popular ? 'text-indigo-100' : 'text-[#6e6e73]'}`}>
                    /mes
                  </span>
                </div>
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-center gap-3 text-sm">
                      <Check className={`w-4 h-4 ${plan.popular ? 'text-white' : 'text-indigo-400'}`} />
                      <span className={plan.popular ? 'text-indigo-50' : 'text-[#a1a1a6]'}>{feature}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/register"
                  className={`block w-full py-3 text-center font-semibold rounded-xl transition-all ${
                    plan.popular 
                      ? 'bg-white text-indigo-600 hover:bg-indigo-50' 
                      : 'nova-btn-secondary border border-white/[0.1]'
                  }`}
                  data-testid={`plan-${plan.name.toLowerCase()}-cta`}
                >
                  Comenzar
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-3xl mx-auto text-center"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            ¿Listo para transformar tu negocio?
          </h2>
          <p className="text-[#a1a1a6] mb-10 max-w-xl mx-auto">
            Únete a miles de negocios que ya usan Nova para gestionar sus operaciones.
          </p>
          <Link to="/register" className="nova-btn nova-btn-primary px-10 py-4 text-base group" data-testid="cta-bottom">
            Comenzar prueba gratis
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </motion.div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-white/[0.04]">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded bg-gradient-to-br from-indigo-500 to-indigo-600 flex items-center justify-center">
              <span className="text-white font-bold text-[10px]">N</span>
            </div>
            <span className="text-sm text-[#6e6e73]">© 2024 Nova. Todos los derechos reservados.</span>
          </div>
          <div className="flex items-center gap-6">
            <a href="#" className="text-sm text-[#6e6e73] hover:text-white transition-colors">Privacidad</a>
            <a href="#" className="text-sm text-[#6e6e73] hover:text-white transition-colors">Términos</a>
            <a href="#" className="text-sm text-[#6e6e73] hover:text-white transition-colors">Contacto</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
